import { HookFormImageUploadInput } from '@realtyka/real-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  AGENT_ACCEPTED_FILE_TYPES,
  AGENT_MAX_FILE_SIZE,
} from '../../../../constants/FileConstants';
import { FieldObject } from '../../../../openapi/atlantis';
import {
  ApplicationControllerApi,
  OnboardingResponseRoleEnum,
} from '../../../../openapi/yenta';
import ErrorService from '../../../../services/ErrorService';
import { verifyAgentLicenseNumber } from '../../../../slices/ApplicationSlice';
import { useAppDispatch, useAppSelector } from '../../../../slices/store';
import { fileWithState } from '../../../../utils/DropboxUtils';
import {
  extractStateFromTitle,
  getLegacyApplicationId,
  getLicenseNumberByState,
} from '../../../../utils/OnboardingHelpers';
import { getYentaConfiguration } from '../../../../utils/OpenapiConfigurationUtils';
import { getInputValidations } from '../../../../utils/Validations';
import { useAgentFileUpload } from '../../../../hooks/useAgentFileUpload';
import { useChecklistItem } from '../../../../hooks/useChecklistItem';
import LicenseStatusBanner from './LicenseStatusBanner';

interface RealEstateLicenseUploadProps {
  name: string;
  field: FieldObject;
  checklistItemId: string;
}

const RealEstateLicenseUpload: React.FC<RealEstateLicenseUploadProps> = ({
  name,
  field,
  checklistItemId,
}) => {
  // Form context hooks
  const { control, setValue, watch } = useFormContext();

  // Redux hooks
  const dispatch = useAppDispatch();
  const {
    auth: { authUserDetail },
    application: { currentQuestion },
  } = useAppSelector((state) => state);

  // Local state
  const [licenseNumber, setLicenseNumber] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  // Computed values
  const legacyApplicationId = getLegacyApplicationId(
    authUserDetail,
    OnboardingResponseRoleEnum.Agent,
  );
  const title = (currentQuestion?.content as any)?.title || '';
  const state = extractStateFromTitle(title);
  const currentFile = watch(name);

  // Use custom hooks for checklist and file upload
  const { files, setFileReferences } = useChecklistItem(checklistItemId);

  // Fetch license number from application
  const fetchLicenseNumber = useCallback(async () => {
    if (!legacyApplicationId || !state) return;

    try {
      const { data } = await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).getApplication(legacyApplicationId);

      const licenseNum = getLicenseNumberByState(
        data.doesBusinessInExtended,
        state,
      );
      setLicenseNumber(licenseNum);
    } catch (error: any) {
      ErrorService.notifyIgnoreAuthErrors(
        'Error fetching license number',
        error,
      );
    }
  }, [state, legacyApplicationId]);

  // Fetch license number on mount
  useEffect(() => {
    if (legacyApplicationId) {
      fetchLicenseNumber();
    }
  }, [legacyApplicationId, fetchLicenseNumber]);

  // License verification callback
  const handleLicenseVerification = useCallback(
    async (file: File) => {
      // Set file with loading state
      const fileWithLoadingState = fileWithState(file, 'loading');
      setValue(name, fileWithLoadingState, { shouldValidate: true });

      // Verify license if available
      if (licenseNumber && authUserDetail?.id) {
        const validationResult = await dispatch(
          verifyAgentLicenseNumber(authUserDetail.id, file, licenseNumber),
        );
        setIsValid(validationResult);
      }

      // Update to attached state after verification
      const fileWithAttachedState = fileWithState(file, 'attached');
      setValue(name, fileWithAttachedState, { shouldValidate: true });
    },
    [licenseNumber, authUserDetail?.id, dispatch, name, setValue],
  );

  // Memoize the reset callback to prevent infinite loops
  const resetAdditionalState = useCallback(() => {
    setIsValid(null);
  }, []);

  const { handleFileChange, handleFileDelete, resetFormState } =
    useAgentFileUpload({
      name,
      checklistItemId,
      files,
      setValue,
      watch,
      setFileReferences,
      validatePDF: true,
      onFileUploadComplete: handleLicenseVerification,
      resetAdditionalState,
    });

  // Reset form state when checklistItemId changes
  useEffect(() => {
    resetFormState();
  }, [checklistItemId, resetFormState]);

  const getFieldFromCurrentQuestion = useCallback(() => {
    // @ts-ignore
    return currentQuestion?.content?.fields?.find((f) => f.id === field.id);
  }, [currentQuestion, field]);

  return (
    <div>
      <HookFormImageUploadInput
        key={checklistItemId}
        name={name}
        control={control}
        label={field.label}
        rules={getInputValidations(field)}
        onChange={handleFileChange}
        onDelete={handleFileDelete}
        accept={AGENT_ACCEPTED_FILE_TYPES}
        maxSize={AGENT_MAX_FILE_SIZE}
        fileTypeHint='Please upload only image or document files up to 5MB.'
      />

      {currentFile && currentFile.state !== 'loading' && (
        <div className='mt-4'>
          {getFieldFromCurrentQuestion()?.answer && isValid === null ? (
            <LicenseStatusBanner variant='pending' />
          ) : isValid === true ? (
            <LicenseStatusBanner variant='info' />
          ) : isValid === false ? (
            <LicenseStatusBanner variant='mismatch' />
          ) : null}
        </div>
      )}
    </div>
  );
};

export default RealEstateLicenseUpload;
